import { createContext, useContext, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

// Context for tab state
interface TabsContextValue {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return context;
}

// Root Tabs component
interface TabsProps {
  children: ReactNode;
  defaultValue: string;
  className?: string;
  onChange?: (value: string) => void;
}

export function Tabs({ children, defaultValue, className, onChange }: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultValue);

  const handleSetActiveTab = (tab: string) => {
    setActiveTab(tab);
    onChange?.(tab);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleSetActiveTab }}>
      <div className={cn('w-full', className)}>{children}</div>
    </TabsContext.Provider>
  );
}

// Tab list container
interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <div
      className={cn(
        'flex gap-1 p-1 bg-bg-tertiary rounded-lg border border-border-default',
        className
      )}
    >
      {children}
    </div>
  );
}

// Individual tab trigger
interface TabsTriggerProps {
  children: ReactNode;
  value: string;
  className?: string;
  icon?: ReactNode;
}

export function TabsTrigger({ children, value, className, icon }: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === value;

  return (
    <button
      type="button"
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all',
        isActive
          ? 'bg-bg-secondary text-accent-blue shadow-sm border border-border-default'
          : 'text-text-secondary hover:text-text-primary hover:bg-bg-secondary/50',
        className
      )}
      onClick={() => setActiveTab(value)}
    >
      {icon}
      {children}
    </button>
  );
}

// Tab content panel
interface TabsContentProps {
  children: ReactNode;
  value: string;
  className?: string;
}

export function TabsContent({ children, value, className }: TabsContentProps) {
  const { activeTab } = useTabsContext();

  if (activeTab !== value) return null;

  return (
    <div className={cn('mt-4', className)}>
      {children}
    </div>
  );
}

